import React from "react";

// The marker-slab highlight from the LetsLoop posters.
//
// On the Instagram renders (LL-0189) the headline sits on torn coral and gold
// slabs rather than on the photograph directly. It is the single most
// recognisable mark in our marketing output and the website had nothing like
// it, which is a large part of why the site reads flatter than the posters.
//
// Implemented as a skewed pseudo-element behind the text rather than a
// background-image, so it:
//   - reflows with the text at any breakpoint (a raster slab cannot),
//   - inherits the theme tokens,
//   - costs no request,
//   - and wraps correctly across lines, which is the part a single rotated div
//     gets wrong.
//
// The slight rotation and the uneven radius are deliberate. A clean rectangle
// reads as a UI chip; the posters read as marker pen, and that difference is
// the whole point.
//
// The CSS lives in styles.css, NOT in a <style> here. Rendered inline it sat
// INSIDE the <h1> being highlighted, so the heading's text content included the
// stylesheet source, which is what a crawler and a screen reader read. It was
// also duplicated once per instance.

type Props = {
  children: React.ReactNode;
  /** Slab colour. `gold` is the secondary from the poster set. */
  tone?: "coral" | "gold" | "mint" | "ink";
  className?: string;
};

const TONES: Record<string, { bg: string; fg: string }> = {
  coral: { bg: "var(--color-salmon)", fg: "var(--color-on-salmon)" },
  gold: { bg: "#F5BE6E", fg: "#2d3849" },
  mint: { bg: "var(--color-paper-mint)", fg: "var(--color-ink)" },
  ink: { bg: "var(--color-ink)", fg: "var(--color-cream)" },
};

const Highlighter: React.FC<Props> = ({
  children,
  tone = "coral",
  className = "",
}) => {
  const t = TONES[tone] ?? TONES.coral;
  return (
    <span
      className={`ll-hl ${className}`}
      style={
        {
          "--hl-bg": t.bg,
          "--hl-fg": t.fg,
        } as React.CSSProperties
      }
    >
      {children}
    </span>
  );
};

export default Highlighter;
